import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article: Article | undefined = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <Link to="/article" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '爱液下载',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '文章资讯', to: '/article' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {cat && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                    {cat.name}
                  </span>
                )}
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-sm"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-sm"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-sm"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-sm"></i>
                  {article.views.toLocaleString()} 阅读
                </span>
              </div>
            </header>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-foreground-700
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:text-sm [&_ul]:text-foreground-700 [&_ul]:space-y-1
                [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4 [&_ol]:text-sm [&_ol]:text-foreground-700 [&_ol]:space-y-1
                [&_li]:leading-relaxed
                [&_strong]:font-semibold [&_strong]:text-foreground-900
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200">
                <h3 className="text-base font-semibold text-foreground-900 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-2">{ra.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}